import { useEffect, useId, useRef, useState } from "react";
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    size,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import {
    AlignCenterHorizontal,
    AlignCenterVertical,
    AlignEndHorizontal,
    AlignEndVertical,
    AlignHorizontalDistributeCenter,
    AlignStartHorizontal,
    AlignStartVertical,
    AlignVerticalDistributeCenter,
    ArrowDown,
    ArrowUp,
    Circle,
    ExternalLink,
    Eye,
    EyeOff,
    Flame,
    Hexagon,
    Layers,
    Link2,
    Lock,
    LockOpen,
    PanelRight,
    Plus,
    Share2,
    Square,
    Trash2,
    Unlink,
    Waypoints,
} from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import type { AlignMode } from "./SvgDiagram2DContext";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import type {
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLayer,
    DiagramNode,
    DiagramPolygon,
    DiagramRect,
    NodeGroup,
} from "../../utils/svgDiagram2DModel";
import { OutlineFieldsEditor } from "./SvgDiagram2DSectionPanels";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { UndoRedoControls } from "../reusable/UndoRedoControls";
import { ShortcutsModal } from "../reusable/ShortcutsModal";
import type { Shortcut } from "../reusable/ShortcutsModal";

const DIAGRAM_SHORTCUTS: Shortcut[] = [
    { keys: ["V"], description: "Select subtool" },
    { keys: ["N"], description: "Place Node subtool" },
    { keys: ["E"], description: "Ellipse subtool" },
    { keys: ["L"], description: "Line subtool" },
    { keys: ["A"], description: "Arc subtool" },
    { keys: ["B"], description: "Rectangle subtool" },
    { keys: ["P"], description: "Polygon subtool" },
    { keys: ["H"], description: "Heat zone subtool" },
    { keys: ["G"], description: "Group subtool" },
    { keys: ["K"], description: "Connect subtool" },
    { keys: ["S"], description: "Scale subtool" },
    { keys: ["R"], description: "Rotate subtool" },
    {
        keys: ["M"],
        description:
            "Toggle snapping (align dragged nodes to other nodes' X/Y and the canvas center/edges)",
    },
    {
        keys: ["drag"],
        description:
            "With the Select subtool, drag empty canvas to rectangle-select nodes and ellipses together",
    },
    {
        keys: ["Shift", "drag"],
        description:
            "With the Ellipse subtool, lock the new ellipse to a circle (1:1)",
    },
    {
        keys: ["Shift", "drag"],
        description:
            "With the Rectangle subtool, lock the new rectangle to a square (1:1)",
    },
    {
        keys: ["Shift", "drag"],
        description:
            "With the Line / Arc subtool, lock the new shape's angle (H / V / 45°)",
    },
    {
        keys: ["click"],
        description:
            "With the Polygon subtool, click to place each vertex; click the first vertex to close",
    },
    {
        keys: ["drag"],
        description:
            "With the Group subtool, drag a rectangle over ungrouped nodes to group them",
    },
    {
        keys: ["Shift", "drag"],
        description:
            "With the Group subtool, drag over one grouped + one ungrouped node to add the ungrouped one to that group",
    },
    {
        keys: ["Alt", "drag"],
        description:
            "With the Group subtool, drag over nodes to remove each from the last group it joined",
    },
    {
        keys: ["Enter"],
        description: "Close the in-progress polygon (or double-click)",
    },
    {
        keys: ["Backspace"],
        description: "Remove the last placed polygon vertex (while drawing)",
    },
    {
        keys: ["Esc"],
        description: "Cancel the in-progress polygon",
    },
    { keys: ["Ctrl", "C"], description: "Copy selected shapes" },
    { keys: ["Ctrl", "X"], description: "Cut selected shapes" },
    { keys: ["Ctrl", "V"], description: "Paste shapes" },
    {
        keys: ["Ctrl", "Alt", "C"],
        description: "Copy node appearance (color, size, shape, effects)",
    },
    {
        keys: ["Ctrl", "Alt", "V"],
        description: "Paste appearance onto selected nodes",
    },
    { keys: ["Delete"], description: "Delete selected shapes" },
    {
        keys: ["Arrows"],
        description: "Nudge selected shapes by 1px (Shift: 10px)",
    },
    {
        keys: ["C"],
        description:
            "Connect selected nodes using the Connect-selection options (2+ selected)",
    },
    {
        keys: ["X"],
        description: "Remove connections between selected nodes (2+ selected)",
    },
    { keys: ["Ctrl", "Z"], description: "Undo" },
    { keys: ["Ctrl", "Y"], description: "Redo" },
    {
        keys: ["Alt", "drag"],
        description:
            "Duplicate a shape (node / ellipse / line / arc / rect / polygon) by dragging it",
    },
    {
        keys: ["Shift", "drag"],
        description: "Lock node dragging to horizontal / vertical / diagonal",
    },
    {
        keys: ["Alt", "A"],
        description: "Align selection left (any shapes — nodes, ellipses, lines, arcs, rects)",
    },
    { keys: ["Alt", "D"], description: "Align selection right" },
    { keys: ["Alt", "W"], description: "Align selection top" },
    { keys: ["Alt", "S"], description: "Align selection bottom" },
    { keys: ["Alt", "H"], description: "Align selection center (horizontal)" },
    { keys: ["Alt", "V"], description: "Align selection middle (vertical)" },
    { keys: ["Middle-drag"], description: "Pan the canvas" },
    { keys: ["Scroll"], description: "Zoom in / out (toward cursor)" },
    {
        keys: ["Scale drag"],
        description:
            "With the Scale subtool and 2+ elements (or an ellipse) selected, drag to scale them about their median",
    },
    {
        keys: ["Rotate drag"],
        description:
            "With the Rotate subtool and 2+ elements selected, drag to rotate them about their median (Shift snaps to 15° steps)",
    },
];

const ALIGN_OPTIONS: Array<{
    mode: AlignMode;
    label: string;
    Icon: typeof AlignStartVertical;
    minSelected: number;
}> = [
    { mode: "left", label: "Align left", Icon: AlignStartVertical, minSelected: 1 },
    { mode: "center-h", label: "Align center", Icon: AlignCenterVertical, minSelected: 1 },
    { mode: "right", label: "Align right", Icon: AlignEndVertical, minSelected: 1 },
    { mode: "top", label: "Align top", Icon: AlignStartHorizontal, minSelected: 1 },
    { mode: "middle-v", label: "Align middle", Icon: AlignCenterHorizontal, minSelected: 1 },
    { mode: "bottom", label: "Align bottom", Icon: AlignEndHorizontal, minSelected: 1 },
    {
        mode: "distribute-h",
        label: "Distribute horizontally",
        Icon: AlignHorizontalDistributeCenter,
        minSelected: 3,
    },
    {
        mode: "distribute-v",
        label: "Distribute vertically",
        Icon: AlignVerticalDistributeCenter,
        minSelected: 3,
    },
];

// --- Node row (thin list entry) ------------------------------------------

/**
 * Compact list entry: selecting / renaming / showing / deleting a node. The
 * full property editor lives in the floating inspector (NodePanel) — clicking
 * a row selects the node and opens it.
 */
function NodeRow({ node }: { node: DiagramNode }) {
    const {
        updateNode,
        removeNode,
        selectedNodeIds,
        setSelectedNodeId,
        setNodePanelOpen,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();

    const isSpawnClone = Boolean(node.spawnedFrom);
    const selected = selectedNodeIds.includes(node.id);
    const hovered = hoveredId === node.id;

    const openInspector = () => {
        setSelectedNodeId(node.id);
        setNodePanelOpen(true);
    };

    return (
        <li
            className={`diagram-node-row${selected ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(node.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === node.id ? null : cur))
            }
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={openInspector}
                title="Select & edit this node"
            >
                <span
                    className="diagram-node-row-swatch"
                    style={{ background: node.color }}
                    aria-hidden
                />
                <span className="diagram-node-row-label">{node.label}</span>
                {isSpawnClone && (
                    <span className="diagram-node-row-tag">spawn</span>
                )}
            </button>
            {!isSpawnClone && (
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => updateNode(node.id, { visible: !node.visible })}
                    title={node.visible ? "Hide node" : "Show node"}
                    style={{ padding: "2px 6px" }}
                >
                    {node.visible ? (
                        <Eye size={14} aria-hidden />
                    ) : (
                        <EyeOff size={14} aria-hidden />
                    )}
                </button>
            )}
            <button
                type="button"
                className="generic-button"
                onClick={() => removeNode(node.id)}
                title="Remove node"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
        </li>
    );
}

// --- Ellipse row (thin list entry) ---------------------------------------

/**
 * Compact list entry for a standalone ellipse: selecting / showing / deleting
 * it. The full editor lives in the floating ellipse inspector — clicking the
 * row selects the ellipse and opens it.
 */
function EllipseRow({ ellipse }: { ellipse: DiagramEllipse }) {
    const {
        updateEllipse,
        removeEllipse,
        selectedEllipseIds,
        setSelectedEllipseId,
        toggleEllipseSelection,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();
    const selected = selectedEllipseIds.includes(ellipse.id);
    const hovered = hoveredId === ellipse.id;

    return (
        <li
            className={`diagram-node-row${selected ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(ellipse.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === ellipse.id ? null : cur))
            }
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={(e) =>
                    e.shiftKey
                        ? toggleEllipseSelection(ellipse.id)
                        : setSelectedEllipseId(ellipse.id)
                }
                title="Select & edit this ellipse (shift to add to selection)"
            >
                <span
                    className="diagram-node-row-swatch"
                    style={{ background: ellipse.color }}
                    aria-hidden
                />
                <span className="diagram-node-row-label">{ellipse.label}</span>
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() =>
                    updateEllipse(ellipse.id, { visible: !ellipse.visible })
                }
                title={ellipse.visible ? "Hide ellipse" : "Show ellipse"}
                style={{ padding: "2px 6px" }}
            >
                {ellipse.visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => removeEllipse(ellipse.id)}
                title="Remove ellipse"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
        </li>
    );
}

// --- Rect row (thin list entry) ------------------------------------------

/**
 * Compact list entry for a standalone rectangle: selecting / showing / deleting
 * it. The full editor lives in the floating rect inspector — clicking the row
 * selects the rect and opens it.
 */
function RectRow({ rect }: { rect: DiagramRect }) {
    const {
        updateRect,
        removeRect,
        selectedRectIds,
        setSelectedRectId,
        toggleRectSelection,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();
    const selected = selectedRectIds.includes(rect.id);
    const hovered = hoveredId === rect.id;

    return (
        <li
            className={`diagram-node-row${selected ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(rect.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === rect.id ? null : cur))
            }
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={(e) =>
                    e.shiftKey
                        ? toggleRectSelection(rect.id)
                        : setSelectedRectId(rect.id)
                }
                title="Select & edit this rectangle (shift to add to selection)"
            >
                <span
                    className="diagram-node-row-swatch"
                    style={{ background: rect.color }}
                    aria-hidden
                />
                <span className="diagram-node-row-label">{rect.label}</span>
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() =>
                    updateRect(rect.id, { visible: !rect.visible })
                }
                title={rect.visible ? "Hide rectangle" : "Show rectangle"}
                style={{ padding: "2px 6px" }}
            >
                {rect.visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => removeRect(rect.id)}
                title="Remove rectangle"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
        </li>
    );
}

// --- Polygon row (thin list entry) ---------------------------------------

/**
 * Compact list entry for a standalone polygon: selecting / showing / deleting
 * it. The full editor lives in the floating polygon inspector — clicking the
 * row selects the polygon and opens it.
 */
function PolygonRow({ polygon }: { polygon: DiagramPolygon }) {
    const {
        updatePolygon,
        removePolygon,
        selectedPolygonIds,
        setSelectedPolygonId,
        togglePolygonSelection,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();
    const selected = selectedPolygonIds.includes(polygon.id);
    const hovered = hoveredId === polygon.id;

    return (
        <li
            className={`diagram-node-row${selected ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(polygon.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === polygon.id ? null : cur))
            }
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={(e) =>
                    e.shiftKey
                        ? togglePolygonSelection(polygon.id)
                        : setSelectedPolygonId(polygon.id)
                }
                title="Select & edit this polygon (shift to add to selection)"
            >
                <span
                    className="diagram-node-row-swatch"
                    style={{ background: polygon.color }}
                    aria-hidden
                />
                <span className="diagram-node-row-label">{polygon.label}</span>
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() =>
                    updatePolygon(polygon.id, { visible: !polygon.visible })
                }
                title={polygon.visible ? "Hide polygon" : "Show polygon"}
                style={{ padding: "2px 6px" }}
            >
                {polygon.visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => removePolygon(polygon.id)}
                title="Remove polygon"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
        </li>
    );
}

// --- Heat zone row (list entry + inline editor) --------------------------

/**
 * List entry for an editor-only heat zone: select / show / delete, plus an
 * inline editor (color + influence radius) revealed when the zone is selected.
 * Heat zones draw nothing on export — they only recolor heat-reactive elements
 * (a stroke style's objects-along-path, a reactive node) by proximity.
 */
function HeatZoneRow({ zone }: { zone: DiagramHeatZone }) {
    const {
        updateHeatZone,
        removeHeatZone,
        selectedHeatZoneIds,
        setSelectedHeatZoneId,
        toggleHeatZoneSelection,
        pushSnapshot,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();
    const selected = selectedHeatZoneIds.includes(zone.id);
    const hovered = hoveredId === zone.id;

    return (
        <li
            className={`diagram-node-row${selected ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(zone.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === zone.id ? null : cur))
            }
            style={{ flexWrap: "wrap" }}
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={(e) =>
                    e.shiftKey
                        ? toggleHeatZoneSelection(zone.id)
                        : setSelectedHeatZoneId(zone.id)
                }
                title="Select & edit this heat zone (shift to add to selection)"
            >
                <span
                    className="diagram-node-row-swatch"
                    style={{ background: zone.color }}
                    aria-hidden
                />
                <span className="diagram-node-row-label">{zone.label}</span>
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() =>
                    updateHeatZone(zone.id, { visible: !zone.visible })
                }
                title={zone.visible ? "Hide heat zone" : "Show heat zone"}
                style={{ padding: "2px 6px" }}
            >
                {zone.visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => removeHeatZone(zone.id)}
                title="Remove heat zone"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
            {selected && (
                <div
                    style={{
                        flexBasis: "100%",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        marginTop: 6,
                    }}
                >
                    <label
                        style={{ display: "flex", alignItems: "center", gap: 4 }}
                        title="Color reactive elements blend toward inside this zone"
                    >
                        Color
                        <input
                            type="color"
                            value={zone.color}
                            onChange={(e) =>
                                updateHeatZone(zone.id, {
                                    color: e.target.value,
                                })
                            }
                            onPointerDown={pushSnapshot}
                        />
                    </label>
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            flex: 1,
                        }}
                        title="How far the influence reaches beyond the zone edge"
                    >
                        Radius
                        <input
                            type="number"
                            min={0}
                            value={zone.radius}
                            onChange={(e) =>
                                updateHeatZone(zone.id, {
                                    radius: Math.max(
                                        0,
                                        Number(e.target.value) || 0,
                                    ),
                                })
                            }
                            onPointerDown={pushSnapshot}
                            style={{ width: 70 }}
                        />
                    </label>
                </div>
            )}
        </li>
    );
}

// --- Layers --------------------------------------------------------------

/**
 * Count of every element + group living on a layer, for the row's summary.
 * Connections aren't counted (they ride their endpoints' layer).
 */
function countLayerContents(
    doc: ReturnType<typeof useSvgDiagram2D>["doc"],
    layerId: string,
): number {
    const on = <T extends { layerId: string }>(arr: T[]) =>
        arr.filter((x) => x.layerId === layerId).length;
    return (
        on(doc.nodes) +
        on(doc.ellipses) +
        on(doc.lines) +
        on(doc.arcs) +
        on(doc.rects) +
        on(doc.polygons) +
        on(doc.groups)
    );
}

/**
 * One row in the Layers list. Layers are listed front-to-back (the topmost row
 * is the front-most layer), so "up" moves a layer toward the front. The active
 * layer (where new elements land) is marked with a radio; visibility and lock
 * toggle the layer; the trash removes it and everything on it.
 */
function LayerRow({
    layer,
    index,
    total,
}: {
    layer: DiagramLayer;
    index: number;
    total: number;
}) {
    const {
        doc,
        activeLayerId,
        setActiveLayerId,
        updateLayer,
        removeLayer,
        moveLayer,
    } = useSvgDiagram2D();

    const isActive = layer.id === activeLayerId;
    const count = countLayerContents(doc, layer.id);

    return (
        <li
            className={`diagram-node-row${isActive ? " selected" : ""}`}
            style={{ gap: 4 }}
        >
            <button
                type="button"
                className="generic-button"
                onClick={() => setActiveLayerId(layer.id)}
                title={
                    isActive
                        ? "Active layer (new elements land here)"
                        : "Make this the active layer"
                }
                aria-pressed={isActive}
                style={{ padding: "2px 6px" }}
            >
                <Layers size={14} aria-hidden />
            </button>
            <input
                type="text"
                value={layer.name}
                onChange={(e) => updateLayer(layer.id, { name: e.target.value })}
                aria-label="Layer name"
                style={{ flex: 1, minWidth: 0 }}
            />
            <span
                className="path-tool-meta"
                style={{ opacity: 0.6, fontVariantNumeric: "tabular-nums" }}
                title="Elements on this layer"
            >
                {count}
            </span>
            <button
                type="button"
                className="generic-button"
                onClick={() => moveLayer(layer.id, "up")}
                disabled={index === 0}
                title="Move layer toward the front"
                style={{ padding: "2px 6px" }}
            >
                <ArrowUp size={14} aria-hidden />
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => moveLayer(layer.id, "down")}
                disabled={index === total - 1}
                title="Move layer toward the back"
                style={{ padding: "2px 6px" }}
            >
                <ArrowDown size={14} aria-hidden />
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() =>
                    updateLayer(layer.id, { visible: !layer.visible })
                }
                title={layer.visible ? "Hide layer" : "Show layer"}
                style={{ padding: "2px 6px" }}
            >
                {layer.visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => updateLayer(layer.id, { locked: !layer.locked })}
                title={layer.locked ? "Unlock layer" : "Lock layer"}
                style={{ padding: "2px 6px" }}
            >
                {layer.locked ? (
                    <Lock size={14} aria-hidden />
                ) : (
                    <LockOpen size={14} aria-hidden />
                )}
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => {
                    if (
                        count === 0 ||
                        window.confirm(
                            `Delete "${layer.name}" and its ${count} element${count === 1 ? "" : "s"}?`,
                        )
                    ) {
                        removeLayer(layer.id);
                    }
                }}
                disabled={total <= 1}
                title={
                    total <= 1
                        ? "A diagram needs at least one layer"
                        : "Delete layer and its contents"
                }
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>
        </li>
    );
}

/**
 * Layers list: every element lives on a layer, layers stack front-to-back, and
 * groups/connections can't span layers. Listed front-most first so reordering
 * reads like a z-stack.
 */
function LayersSection() {
    const {
        doc,
        addLayer,
        moveSelectionToLayer,
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
    } = useSvgDiagram2D();

    const hasSelection =
        selectedNodeIds.length +
            selectedEllipseIds.length +
            selectedLineIds.length +
            selectedArcIds.length +
            selectedRectIds.length +
            selectedPolygonIds.length >
        0;

    // Front-most layer first (the last entry in doc.layers draws on top).
    const frontFirst = [...doc.layers].reverse();

    return (
        <>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {frontFirst.map((layer, i) => (
                    <LayerRow
                        key={layer.id}
                        layer={layer}
                        index={i}
                        total={doc.layers.length}
                    />
                ))}
            </ul>
            <div className="field-row" style={{ gap: 4, marginTop: 6 }}>
                <button
                    type="button"
                    className="generic-button"
                    onClick={addLayer}
                    title="Add a new layer (becomes the active layer)"
                >
                    <Plus size={14} aria-hidden /> Add layer
                </button>
                {hasSelection && doc.layers.length > 1 && (
                    <label className="field-label" style={{ marginLeft: "auto" }}>
                        Move selection to
                        <select
                            className="anchor-select"
                            value=""
                            onChange={(e) => {
                                if (e.target.value) {
                                    moveSelectionToLayer(e.target.value);
                                }
                            }}
                        >
                            <option value="">Choose layer…</option>
                            {[...doc.layers].reverse().map((l) => (
                                <option key={l.id} value={l.id}>
                                    {l.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
            <p className="path-tool-meta" style={{ margin: "6px 0 0", opacity: 0.7 }}>
                New elements are placed on the active layer (
                <Layers size={12} aria-hidden style={{ verticalAlign: "-1px" }} />
                ). Groups and connections can't span layers.
            </p>
        </>
    );
}

// --- Group row -----------------------------------------------------------

/**
 * Anchored popover used by the group list: opens next to its trigger row,
 * portals to <body>, and closes on outside-click / Escape. Positioning is
 * delegated to Floating UI off the row element, so it flips above the row and
 * caps its height near the viewport's bottom edge instead of clipping.
 */
function RowPopover({
    open,
    onClose,
    anchorRef,
    label,
    children,
}: {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement | null>;
    label: string;
    children: React.ReactNode;
}) {
    const titleId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: (next) => {
            if (!next) onClose();
        },
        placement: "bottom-start",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip(),
            shift({ padding: 8 }),
            size({
                padding: 8,
                apply({ availableWidth, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${Math.min(320, availableWidth)}px`,
                        maxWidth: `${Math.min(320, availableWidth)}px`,
                        // Cap to the space on the chosen side; the body scrolls.
                        maxHeight: `${availableHeight}px`,
                    });
                },
            }),
        ],
    });

    // The trigger row is owned by the caller; feed it to Floating UI as the
    // position reference. Setting it also makes useDismiss treat clicks on the
    // row as "inside", so toggling the row off doesn't immediately reopen.
    useEffect(() => {
        refs.setPositionReference(anchorRef.current);
    }, [refs, anchorRef, open]);

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getFloatingProps } = useInteractions([dismiss, role]);

    if (!open) return null;

    return (
        <FloatingPortal>
            <div
                ref={refs.setFloating}
                id={titleId}
                className="tool-description-popover__panel"
                aria-label={label}
                style={{ ...floatingStyles, zIndex: 10050 }}
                {...getFloatingProps()}
            >
                <div className="tool-description-popover__body">{children}</div>
            </div>
        </FloatingPortal>
    );
}

/**
 * One group as a thin list entry (name + member count). Clicking the row opens
 * a popover holding the full group editor — rename, member summary, and outline
 * properties — so the list stays compact even with many groups.
 */
function GroupRow({ group }: { group: NodeGroup }) {
    const {
        doc,
        updateGroup,
        removeGroup,
        addGroupProperty,
        updateGroupProperty,
        removeGroupProperty,
        groupRevealRequest,
        revealConfigSection,
        hoveredId,
        setHoveredId,
    } = useSvgDiagram2D();

    const [open, setOpen] = useState(false);
    const rowRef = useRef<HTMLLIElement>(null);
    const hovered = hoveredId === group.id;

    // Honor a "reveal this group" request (e.g. the canvas context menu's
    // "Select <group>"): scroll the row into view and open its editor popover.
    // Keyed on the request nonce so re-selecting the same group re-fires.
    useEffect(() => {
        if (groupRevealRequest?.id !== group.id) return;
        rowRef.current?.scrollIntoView({ block: "nearest" });
        setOpen(true);
    }, [groupRevealRequest, group.id]);

    const memberLabels = group.nodeIds
        .map((id) => doc.nodes.find((n) => n.id === id)?.label)
        .filter(Boolean)
        .join(", ");

    return (
        <li
            ref={rowRef}
            className={`diagram-node-row${open ? " selected" : ""}${hovered ? " hovered" : ""}`}
            onPointerEnter={() => setHoveredId(group.id)}
            onPointerLeave={() =>
                setHoveredId((cur) => (cur === group.id ? null : cur))
            }
        >
            <button
                type="button"
                className="diagram-node-row-main"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Edit this group"
            >
                <span className="diagram-node-row-label">{group.name}</span>
                <span
                    className="diagram-node-row-tag"
                    title="Member nodes"
                >
                    {group.nodeIds.length}
                </span>
            </button>
            <button
                type="button"
                className="generic-button"
                onClick={() => removeGroup(group.id)}
                title="Remove group"
                style={{ padding: "2px 6px" }}
            >
                <Trash2 size={14} aria-hidden />
            </button>

            <RowPopover
                open={open}
                onClose={() => setOpen(false)}
                anchorRef={rowRef}
                label={`Group: ${group.name}`}
            >
                <label className="field-label">
                    Name
                    <input
                        type="text"
                        value={group.name}
                        onChange={(e) =>
                            updateGroup(group.id, { name: e.target.value })
                        }
                        aria-label="Group name"
                        style={{ width: "100%" }}
                    />
                </label>
                <p
                    className="path-tool-meta"
                    style={{ margin: "6px 0", opacity: 0.7 }}
                >
                    {group.nodeIds.length} node
                    {group.nodeIds.length === 1 ? "" : "s"}:{" "}
                    {memberLabels || "—"}
                </p>

                {group.properties.map((prop, i) =>
                    prop.kind === "outline" ? (
                        <div
                            key={i}
                            style={{
                                marginTop: 6,
                                paddingTop: 6,
                                borderTop:
                                    "1px dashed var(--control-border, #3a3a4a)",
                            }}
                        >
                            <div
                                className="field-row"
                                style={{ justifyContent: "space-between" }}
                            >
                                <span
                                    className="path-tool-meta"
                                    style={{ fontWeight: 600 }}
                                >
                                    Outline
                                </span>
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() =>
                                        removeGroupProperty(group.id, i)
                                    }
                                    title="Remove property"
                                    style={{ padding: "2px 6px" }}
                                >
                                    <Trash2 size={14} aria-hidden />
                                </button>
                            </div>
                            <div
                                className="field-row"
                                style={{ gap: 4, alignItems: "flex-end" }}
                            >
                                {doc.groupOutlinePresets.length > 0 && (
                                    <label
                                        className="field-label"
                                        style={{ flex: 1, minWidth: 0 }}
                                    >
                                        Linked preset
                                        <select
                                            className="anchor-select"
                                            value={prop.presetId ?? ""}
                                            onChange={(e) =>
                                                updateGroupProperty(group.id, i, {
                                                    presetId:
                                                        e.target.value || null,
                                                })
                                            }
                                        >
                                            <option value="">
                                                None (own settings)
                                            </option>
                                            {doc.groupOutlinePresets.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() =>
                                        revealConfigSection(
                                            "group-outline-presets",
                                        )
                                    }
                                    title="Open the Group outline presets panel"
                                    style={{ padding: "2px 6px" }}
                                >
                                    <ExternalLink size={14} aria-hidden />
                                </button>
                            </div>
                            {doc.groupOutlinePresets.length === 0 && (
                                <p
                                    className="path-tool-meta"
                                    style={{ margin: "4px 0 0", opacity: 0.7 }}
                                >
                                    No outline presets yet — open the presets
                                    panel to create one.
                                </p>
                            )}
                            {prop.presetId ? (
                                <p
                                    className="path-tool-meta"
                                    style={{ margin: "4px 0 0", opacity: 0.7 }}
                                >
                                    Styled by the linked preset. Set to “None” to
                                    edit this group's outline on its own.
                                </p>
                            ) : (
                                <OutlineFieldsEditor
                                    fields={prop}
                                    strokeStyles={doc.strokeStyles}
                                    patterns={doc.patterns}
                                    onChange={(partial) =>
                                        updateGroupProperty(group.id, i, partial)
                                    }
                                />
                            )}
                        </div>
                    ) : null,
                )}
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => addGroupProperty(group.id, "outline")}
                    style={{ marginTop: 6 }}
                >
                    <Plus size={14} aria-hidden /> Outline
                </button>
            </RowPopover>
        </li>
    );
}

// --- Main controls -------------------------------------------------------

export function SvgDiagram2DControls() {
    const {
        doc,
        setTool,
        selectedNodeIds,
        connectSelectedNodes,
        clearConnectionsBetweenSelected,
        copySelectedNodes,
        cutSelectedNodes,
        pasteNodes,
        copySelectedNodeStyle,
        pasteNodeStyle,
        removeSelectedNodes,
        alignSelectedNodes,
        setNodePanelOpen,
        selectedEllipseIds,
        removeSelectedEllipses,
        selectedLineIds,
        removeSelectedLines,
        selectedArcIds,
        removeSelectedArcs,
        selectedRectIds,
        removeSelectedRects,
        selectedPolygonIds,
        removeSelectedPolygons,
        selectedHeatZoneIds,
        removeSelectedHeatZones,
        moveNodes,
        moveEllipses,
        moveLines,
        moveArcs,
        moveRects,
        movePolygons,
        moveHeatZones,
        pushSnapshot,
        activePresetId,
        createGroupFromSelection,
        groupRevealRequest,
        undo,
        redo,
        canUndo,
        canRedo,
        toggleSnapEnabled,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
        saveProjectToFile,
        loadProjectFromFile,
    } = useSvgDiagram2D();

    useUndoRedoKeyboard({ undo, redo });

    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [hideSpawned, setHideSpawned] = useState(false);
    const projectInputRef = useRef<HTMLInputElement>(null);

    // The Groups section is collapsible; track its open state here so a "reveal
    // group" request (from the canvas context menu) can force it open before the
    // target row scrolls itself into view and opens its popover.
    const [groupsOpen, setGroupsOpen] = useState(true);
    useEffect(() => {
        if (groupRevealRequest) setGroupsOpen(true);
    }, [groupRevealRequest]);

    // Tool-level keyboard shortcuts: copy/paste nodes and align the selection.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target;
            const editable =
                target instanceof HTMLElement &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.tagName === "SELECT" ||
                    target.isContentEditable);
            if (editable) return;

            // Ctrl+Alt+C / Ctrl+Alt+V: copy/paste node appearance only.
            if ((e.ctrlKey || e.metaKey) && e.altKey && !e.shiftKey) {
                if (e.code === "KeyC") {
                    if (selectedNodeIds.length === 0) return;
                    e.preventDefault();
                    copySelectedNodeStyle();
                } else if (e.code === "KeyV") {
                    if (selectedNodeIds.length === 0) return;
                    e.preventDefault();
                    pasteNodeStyle();
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
                // Copy / cut act on every selected shape type, not just nodes.
                const hasSelection =
                    selectedNodeIds.length > 0 ||
                    selectedEllipseIds.length > 0 ||
                    selectedLineIds.length > 0 ||
                    selectedArcIds.length > 0 ||
                    selectedRectIds.length > 0;
                if (e.code === "KeyC") {
                    if (!hasSelection) return;
                    e.preventDefault();
                    copySelectedNodes();
                } else if (e.code === "KeyX") {
                    if (!hasSelection) return;
                    e.preventDefault();
                    cutSelectedNodes();
                } else if (e.code === "KeyV") {
                    e.preventDefault();
                    pasteNodes();
                }
                return;
            }

            if (
                (e.key === "Delete" || e.key === "Backspace") &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
            ) {
                if (
                    selectedNodeIds.length === 0 &&
                    selectedEllipseIds.length === 0 &&
                    selectedLineIds.length === 0 &&
                    selectedArcIds.length === 0 &&
                    selectedRectIds.length === 0 &&
                    selectedPolygonIds.length === 0 &&
                    selectedHeatZoneIds.length === 0
                ) {
                    return;
                }
                e.preventDefault();
                // Delete everything selected — every shape type together.
                if (selectedNodeIds.length > 0) removeSelectedNodes();
                if (selectedEllipseIds.length > 0) removeSelectedEllipses();
                if (selectedLineIds.length > 0) removeSelectedLines();
                if (selectedArcIds.length > 0) removeSelectedArcs();
                if (selectedRectIds.length > 0) removeSelectedRects();
                if (selectedPolygonIds.length > 0) removeSelectedPolygons();
                if (selectedHeatZoneIds.length > 0) removeSelectedHeatZones();
                return;
            }

            // Arrow keys nudge the selection: 1px per press, 10px with Shift.
            // Allows Shift (but no Ctrl/Meta/Alt) so it must run before the
            // plain-keys block below, which rejects Shift.
            if (
                (e.key === "ArrowUp" ||
                    e.key === "ArrowDown" ||
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight") &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
            ) {
                const step = e.shiftKey ? 10 : 1;
                const dx =
                    e.key === "ArrowLeft"
                        ? -step
                        : e.key === "ArrowRight"
                          ? step
                          : 0;
                const dy =
                    e.key === "ArrowUp"
                        ? -step
                        : e.key === "ArrowDown"
                          ? step
                          : 0;
                const hasSelection =
                    selectedNodeIds.length > 0 ||
                    selectedEllipseIds.length > 0 ||
                    selectedLineIds.length > 0 ||
                    selectedArcIds.length > 0 ||
                    selectedRectIds.length > 0 ||
                    selectedPolygonIds.length > 0 ||
                    selectedHeatZoneIds.length > 0;
                if (!hasSelection) return;
                e.preventDefault();
                pushSnapshot();
                const nodeSel = new Set(selectedNodeIds);
                const ellipseSel = new Set(selectedEllipseIds);
                const lineSel = new Set(selectedLineIds);
                const arcSel = new Set(selectedArcIds);
                const rectSel = new Set(selectedRectIds);
                const polygonSel = new Set(selectedPolygonIds);
                const heatZoneSel = new Set(selectedHeatZoneIds);
                if (nodeSel.size > 0) {
                    moveNodes(
                        doc.nodes
                            .filter((n) => nodeSel.has(n.id))
                            .map((n) => ({
                                id: n.id,
                                pos: {
                                    x: n.position.x + dx,
                                    y: n.position.y + dy,
                                },
                            })),
                    );
                }
                if (ellipseSel.size > 0) {
                    moveEllipses(
                        doc.ellipses
                            .filter((el) => ellipseSel.has(el.id))
                            .map((el) => ({
                                id: el.id,
                                center: {
                                    x: el.center.x + dx,
                                    y: el.center.y + dy,
                                },
                            })),
                    );
                }
                if (lineSel.size > 0) {
                    moveLines(
                        doc.lines
                            .filter((l) => lineSel.has(l.id))
                            .map((l) => ({
                                id: l.id,
                                start: { x: l.start.x + dx, y: l.start.y + dy },
                                end: { x: l.end.x + dx, y: l.end.y + dy },
                            })),
                    );
                }
                if (arcSel.size > 0) {
                    moveArcs(
                        doc.arcs
                            .filter((a) => arcSel.has(a.id))
                            .map((a) => ({
                                id: a.id,
                                start: { x: a.start.x + dx, y: a.start.y + dy },
                                end: { x: a.end.x + dx, y: a.end.y + dy },
                            })),
                    );
                }
                if (rectSel.size > 0) {
                    moveRects(
                        doc.rects
                            .filter((r) => rectSel.has(r.id))
                            .map((r) => ({
                                id: r.id,
                                start: { x: r.start.x + dx, y: r.start.y + dy },
                                end: { x: r.end.x + dx, y: r.end.y + dy },
                            })),
                    );
                }
                if (polygonSel.size > 0) {
                    movePolygons(
                        doc.polygons
                            .filter((p) => polygonSel.has(p.id))
                            .map((p) => ({
                                id: p.id,
                                points: p.points.map((pt) => ({
                                    x: pt.x + dx,
                                    y: pt.y + dy,
                                })),
                            })),
                    );
                }
                if (heatZoneSel.size > 0) {
                    moveHeatZones(
                        doc.heatZones
                            .filter((hz) => heatZoneSel.has(hz.id))
                            .map((hz) => ({
                                id: hz.id,
                                start: {
                                    x: hz.start.x + dx,
                                    y: hz.start.y + dy,
                                },
                                end: { x: hz.end.x + dx, y: hz.end.y + dy },
                            })),
                    );
                }
                return;
            }

            // Plain keys (no modifiers): subtool switches and selection ops.
            // (Ctrl+C / Ctrl+X copy / cut nodes and are handled above.)
            if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                if (e.code === "KeyV") {
                    e.preventDefault();
                    setTool("select");
                    return;
                }
                if (e.code === "KeyN") {
                    e.preventDefault();
                    setTool("place-node");
                    return;
                }
                if (e.code === "KeyD") {
                    e.preventDefault();
                    setTool("node-brush");
                    return;
                }
                if (e.code === "KeyF") {
                    e.preventDefault();
                    setTool("multi-node-brush");
                    return;
                }
                if (e.code === "KeyJ") {
                    e.preventDefault();
                    setTool("mega-node-brush");
                    return;
                }
                if (e.code === "KeyI") {
                    e.preventDefault();
                    setTool("interact");
                    return;
                }
                if (e.code === "KeyE") {
                    e.preventDefault();
                    setTool("ellipse");
                    return;
                }
                if (e.code === "KeyL") {
                    e.preventDefault();
                    setTool("line");
                    return;
                }
                if (e.code === "KeyA") {
                    e.preventDefault();
                    setTool("arc");
                    return;
                }
                if (e.code === "KeyB") {
                    e.preventDefault();
                    setTool("rect");
                    return;
                }
                if (e.code === "KeyP") {
                    e.preventDefault();
                    setTool("polygon");
                    return;
                }
                if (e.code === "KeyH") {
                    e.preventDefault();
                    setTool("heat-zone");
                    return;
                }
                if (e.code === "KeyG") {
                    e.preventDefault();
                    setTool("group");
                    return;
                }
                if (e.code === "KeyK") {
                    e.preventDefault();
                    setTool("connect");
                    return;
                }
                if (e.code === "KeyS") {
                    e.preventDefault();
                    setTool("scale");
                    return;
                }
                if (e.code === "KeyR") {
                    e.preventDefault();
                    setTool("rotate");
                    return;
                }
                if (e.code === "KeyM") {
                    e.preventDefault();
                    toggleSnapEnabled();
                    return;
                }
                if (e.code === "KeyC") {
                    if (selectedNodeIds.length < 2) return;
                    e.preventDefault();
                    connectSelectedNodes();
                    return;
                }
                if (e.code === "KeyX") {
                    if (selectedNodeIds.length < 2) return;
                    e.preventDefault();
                    clearConnectionsBetweenSelected();
                    return;
                }
            }

            if (e.altKey && !e.ctrlKey && !e.metaKey) {
                const map: Record<string, AlignMode> = {
                    KeyA: "left",
                    KeyD: "right",
                    KeyW: "top",
                    KeyS: "bottom",
                    KeyH: "center-h",
                    KeyV: "middle-v",
                };
                const mode = map[e.code];
                if (mode) {
                    e.preventDefault();
                    alignSelectedNodes(mode);
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [
        setTool,
        selectedNodeIds,
        connectSelectedNodes,
        clearConnectionsBetweenSelected,
        copySelectedNodes,
        cutSelectedNodes,
        pasteNodes,
        copySelectedNodeStyle,
        pasteNodeStyle,
        removeSelectedNodes,
        alignSelectedNodes,
        selectedEllipseIds,
        removeSelectedEllipses,
        selectedLineIds,
        removeSelectedLines,
        selectedArcIds,
        removeSelectedArcs,
        selectedRectIds,
        removeSelectedRects,
        selectedPolygonIds,
        removeSelectedPolygons,
        selectedHeatZoneIds,
        removeSelectedHeatZones,
        toggleSnapEnabled,
        doc,
        moveNodes,
        moveEllipses,
        moveLines,
        moveArcs,
        moveRects,
        movePolygons,
        moveHeatZones,
        pushSnapshot,
    ]);

    // Alignment treats every selected shape uniformly (by bounding box), so the
    // align row counts nodes, ellipses, lines, arcs, and rects together. Spawn
    // clones are derived and can't be aligned, so they're excluded.
    const alignableCount =
        doc.nodes.filter(
            (n) => selectedNodeIds.includes(n.id) && !n.spawnedFrom,
        ).length +
        selectedEllipseIds.length +
        selectedLineIds.length +
        selectedArcIds.length +
        selectedRectIds.length +
        selectedPolygonIds.length;

    const spawnedNodeCount = doc.nodes.filter((n) => n.spawnedFrom).length;
    const visibleNodes = hideSpawned
        ? doc.nodes.filter((n) => !n.spawnedFrom)
        : doc.nodes;

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>2D SVG Diagram</h2>
                    <ToolDescriptionPopover dialogLabel="2D SVG Diagram tool description">
                        <p>
                            Author node-and-connection diagrams directly in 2D:
                            place nodes on the canvas, connect them with reusable
                            line presets, stack node/group properties (radial
                            emitters, group outlines), and mirror placement across
                            the canvas's X/Y centerlines. The canvas IS the
                            exported SVG — no projection step required. Rectangle-
                            select or shift-click to select multiple nodes, then
                            group them, connect them all to each other, or use the
                            Scale / Rotate subtools to resize or spin any
                            selection — nodes, ellipses, lines, and arcs — about
                            its median. The
                            Ellipse subtool adds
                            standalone ellipse shapes (drag to draw, Shift for a
                            circle) with adjustable start-angle/sweep arcs.
                        </p>
                        <p>
                            <strong>Shortcuts:</strong> Ctrl+C / Ctrl+X / Ctrl+V
                            copy / cut / paste selected shapes (nodes, ellipses,
                            lines, arcs); Delete removes them; with 2+ nodes
                            selected, C connects them (using the Connect-selection
                            subtool's pattern and proximity options) and X removes
                            connections between them; Alt-drag any shape to
                            duplicate it; with
                            any shapes selected, Alt+A / Alt+D / Alt+W / Alt+S align
                            left / right / top / bottom and Alt+H / Alt+V center
                            horizontally / vertically.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => setShortcutsOpen(true)}
                        aria-haspopup="dialog"
                        aria-controls="svg-diagram-2d-shortcuts"
                    >
                        Shortcuts
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={saveProjectToFile}
                        title="Save the whole diagram (nodes, shapes, presets, canvas) to a .json file"
                    >
                        Save file
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => projectInputRef.current?.click()}
                        title="Load a diagram from a saved .json project file"
                    >
                        Load file
                    </button>
                    <input
                        ref={projectInputRef}
                        type="file"
                        accept=".json,application/json"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) loadProjectFromFile(file);
                            // Reset so picking the same file again re-fires change.
                            e.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <ShortcutsModal
                open={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
                shortcuts={DIAGRAM_SHORTCUTS}
                dialogId="svg-diagram-2d-shortcuts"
                title="2D Diagram shortcuts"
            />

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="svg-diagram-2d"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                {selectedNodeIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedNodeIds.length} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => setNodePanelOpen(true)}
                            title="Open the inspector panel for the first selected node"
                            style={{ marginLeft: "auto" }}
                        >
                            <PanelRight size={14} aria-hidden /> Open panel
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            disabled={selectedNodeIds.length < 2 || !activePresetId}
                            onClick={connectSelectedNodes}
                            title="Connect the selected nodes using the Connect-selection subtool's pattern and proximity options"
                        >
                            <Link2 size={14} aria-hidden /> Connect selected
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            disabled={selectedNodeIds.length < 2}
                            onClick={clearConnectionsBetweenSelected}
                            title="Remove connections between selected nodes"
                        >
                            <Unlink size={14} aria-hidden /> Clear connections
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedNodes}
                            title="Delete selected nodes (Delete)"
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {selectedEllipseIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedEllipseIds.length} ellipse
                            {selectedEllipseIds.length === 1 ? "" : "s"} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedEllipses}
                            title="Delete selected ellipses (Delete)"
                            style={{ marginLeft: "auto" }}
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {selectedLineIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedLineIds.length} line
                            {selectedLineIds.length === 1 ? "" : "s"} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedLines}
                            title="Delete selected lines (Delete)"
                            style={{ marginLeft: "auto" }}
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {selectedArcIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedArcIds.length} arc
                            {selectedArcIds.length === 1 ? "" : "s"} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedArcs}
                            title="Delete selected arcs (Delete)"
                            style={{ marginLeft: "auto" }}
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {selectedRectIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedRectIds.length} rectangle
                            {selectedRectIds.length === 1 ? "" : "s"} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedRects}
                            title="Delete selected rectangles (Delete)"
                            style={{ marginLeft: "auto" }}
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {selectedPolygonIds.length > 0 && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center" }}
                    >
                        <span className="path-tool-meta">
                            {selectedPolygonIds.length} polygon
                            {selectedPolygonIds.length === 1 ? "" : "s"} selected
                        </span>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={removeSelectedPolygons}
                            title="Delete selected polygons (Delete)"
                            style={{ marginLeft: "auto" }}
                        >
                            <Trash2 size={14} aria-hidden /> Delete
                        </button>
                    </div>
                )}

                {alignableCount >= 1 && (
                    <div className="field-row" style={{ gap: 4, flexWrap: "wrap" }}>
                        {ALIGN_OPTIONS.map(({ mode, label, Icon, minSelected }) => {
                            const isDistribute =
                                mode === "distribute-h" || mode === "distribute-v";
                            // A lone element aligns to the canvas; 2+ align to each other.
                            const toCanvas =
                                alignableCount === 1 && !isDistribute;
                            return (
                                <button
                                    key={mode}
                                    type="button"
                                    className="generic-button"
                                    disabled={alignableCount < minSelected}
                                    onClick={() => alignSelectedNodes(mode)}
                                    title={toCanvas ? `${label} of canvas` : label}
                                    style={{ padding: "4px 6px" }}
                                >
                                    <Icon size={14} aria-hidden />
                                </button>
                            );
                        })}
                    </div>
                )}

                <CollapsibleToolGroup
                    title={`Layers (${doc.layers.length})`}
                    icon={Layers}
                >
                    <LayersSection />
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title={`Nodes (${doc.nodes.length})`} icon={Waypoints}>
                    {doc.nodes.length === 0 ? (
                        <p className="path-tool-meta">
                            Use Place Node, then click the canvas in the preview.
                        </p>
                    ) : (
                        <>
                            {spawnedNodeCount > 0 && (
                                <div
                                    className="field-row"
                                    style={{ margin: "0 0 8px" }}
                                >
                                    <CheckboxField
                                        checked={hideSpawned}
                                        onChange={setHideSpawned}
                                        label={`Hide spawned clones (${spawnedNodeCount})`}
                                        inline
                                    />
                                </div>
                            )}
                            {visibleNodes.length === 0 ? (
                                <p className="path-tool-meta">
                                    All nodes are spawned clones (hidden).
                                </p>
                            ) : (
                                <ul
                                    style={{
                                        listStyle: "none",
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    {visibleNodes.map((n) => (
                                        <NodeRow key={n.id} node={n} />
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title={`Ellipses (${doc.ellipses.length})`}
                    icon={Circle}
                >
                    {doc.ellipses.length === 0 ? (
                        <p className="path-tool-meta">
                            Use the Ellipse subtool, then drag on the canvas
                            (hold Shift for a circle).
                        </p>
                    ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            {doc.ellipses.map((e) => (
                                <EllipseRow key={e.id} ellipse={e} />
                            ))}
                        </ul>
                    )}
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => setTool("ellipse")}
                        style={{ marginTop: 6 }}
                    >
                        <Circle size={16} aria-hidden /> Draw ellipse
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title={`Rectangles (${doc.rects.length})`}
                    icon={Square}
                >
                    {doc.rects.length === 0 ? (
                        <p className="path-tool-meta">
                            Use the Rectangle subtool, then drag on the canvas
                            (hold Shift for a square).
                        </p>
                    ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            {doc.rects.map((r) => (
                                <RectRow key={r.id} rect={r} />
                            ))}
                        </ul>
                    )}
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => setTool("rect")}
                        style={{ marginTop: 6 }}
                    >
                        <Square size={16} aria-hidden /> Draw rectangle
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title={`Polygons (${doc.polygons.length})`}
                    icon={Hexagon}
                >
                    {doc.polygons.length === 0 ? (
                        <p className="path-tool-meta">
                            Use the Polygon subtool, then click the canvas to
                            place each vertex; click the first vertex (or press
                            Enter) to close the shape.
                        </p>
                    ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            {doc.polygons.map((p) => (
                                <PolygonRow key={p.id} polygon={p} />
                            ))}
                        </ul>
                    )}
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => setTool("polygon")}
                        style={{ marginTop: 6 }}
                    >
                        <Hexagon size={16} aria-hidden /> Draw polygon
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title={`Heat zones (${doc.heatZones.length})`}
                    icon={Flame}
                >
                    {doc.heatZones.length === 0 ? (
                        <p className="path-tool-meta">
                            Use the Heat zone subtool, then drag on the canvas.
                            Zones aren't exported — they recolor elements set to
                            react to heat (a stroke style's objects-along-path,
                            or a node) by proximity.
                        </p>
                    ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            {doc.heatZones.map((z) => (
                                <HeatZoneRow key={z.id} zone={z} />
                            ))}
                        </ul>
                    )}
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => setTool("heat-zone")}
                        style={{ marginTop: 6 }}
                    >
                        <Flame size={16} aria-hidden /> Draw heat zone
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title={`Groups (${doc.groups.length})`}
                    icon={Waypoints}
                    open={groupsOpen}
                    onOpenChange={setGroupsOpen}
                >
                    <button
                        type="button"
                        className="generic-button"
                        disabled={selectedNodeIds.length === 0}
                        onClick={() => createGroupFromSelection(selectedNodeIds)}
                        title="Create a group from the selected node(s)"
                    >
                        <Plus size={16} aria-hidden /> Group selected (
                        {selectedNodeIds.length})
                    </button>
                    <p className="path-tool-meta" style={{ margin: "6px 0" }}>
                        Click a group below to rename it and edit its outline.
                        Add nodes to a group by selecting them (rectangle-select
                        or shift-click) and grouping, or use the Group subtool
                        (G): drag a rectangle over ungrouped nodes to group them,
                        Shift-drag over one grouped + one ungrouped node to add
                        it, Alt-drag over nodes to remove each from its last
                        group.
                    </p>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {doc.groups.map((g) => (
                            <GroupRow key={g.id} group={g} />
                        ))}
                    </ul>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "svg-diagram-2d-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["svg-diagram-2d"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
